/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef, useCallback } from "react";

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// Authentication context
import { useAuth } from "context/AuthContext";
import { API_ENDPOINTS } from "config/api";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Icon from "@mui/material/Icon";

// @mui icons
import FacebookIcon from "@mui/icons-material/Facebook";
import GitHubIcon from "@mui/icons-material/GitHub";
import GoogleIcon from "@mui/icons-material/Google";
import TwitterIcon from "@mui/icons-material/Twitter";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RefreshIcon from "@mui/icons-material/Refresh";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import Logo from "components/Logo";

// const CAPTCHA_SECRET = process.env.REACT_APP_CAPTCHA_SECRET || "";
// const sha256 = async (message) => { ... };

// Instant 0ms PNG Bitmap CAPTCHA Generator (Zero Text Elements in DOM, Un-copyable Bitmap)
const generateInstantPngCaptcha = (code) => {
  const canvas = document.createElement("canvas");
  canvas.width = 270;
  canvas.height = 48;
  const ctx = canvas.getContext("2d");
  const width = canvas.width;
  const height = canvas.height;

  // 1. Dynamic Gradient Background
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, "#f8fafc");
  gradient.addColorStop(0.5, "#edf2f7");
  gradient.addColorStop(1, "#f1f5f9");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  // 2. Intersecting Interference Lines
  for (let i = 0; i < 6; i++) {
    ctx.strokeStyle = `rgba(${Math.floor(Math.random() * 140)}, ${Math.floor(Math.random() * 140)}, ${Math.floor(Math.random() * 140)}, 0.25)`;
    ctx.lineWidth = Math.random() * 1.5 + 1;
    ctx.beginPath();
    ctx.moveTo(Math.random() * width, Math.random() * height);
    ctx.bezierCurveTo(
      Math.random() * width, Math.random() * height,
      Math.random() * width, Math.random() * height,
      Math.random() * width, Math.random() * height
    );
    ctx.stroke();
  }

  // 3. Multi-font Crisp Characters (Painted as raw pixels)
  const fontFamilies = [
    '"Trebuchet MS", sans-serif',
    '"Segoe UI", sans-serif',
    '"Arial", sans-serif',
    '"Verdana", sans-serif'
  ];
  const colors = ["#0f172a", "#1e1b4b", "#6b21a8", "#0369a1", "#831843", "#047857", "#3730a3"];

  const charWidth = width / (code.length + 0.8);
  for (let i = 0; i < code.length; i++) {
    const char = code[i];
    ctx.save();

    const fontSize = Math.floor(Math.random() * 3) + 26;
    const fontFam = fontFamilies[Math.floor(Math.random() * fontFamilies.length)];
    ctx.font = `bold ${fontSize}px ${fontFam}`;
    ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];

    const x = (i + 0.55) * charWidth;
    const y = height / 2;
    const angle = (Math.random() - 0.5) * 0.22;

    ctx.translate(x, y);
    ctx.rotate(angle);

    const shearX = (Math.random() - 0.5) * 0.15;
    ctx.transform(1, 0, shearX, 1, 0, 0);

    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillText(char, 0, 0);
    ctx.restore();
  }

  // 4. Subtle Noise Dots
  for (let i = 0; i < 30; i++) {
    ctx.fillStyle = `rgba(${Math.floor(Math.random() * 200)}, ${Math.floor(Math.random() * 200)}, ${Math.floor(Math.random() * 200)}, 0.4)`;
    ctx.beginPath();
    ctx.arc(Math.random() * width, Math.random() * height, Math.random() * 1.8, 0, Math.PI * 2);
    ctx.fill();
  }

  // Export as un-copyable PNG Bitmap Data URL
  return canvas.toDataURL("image/png");
};

function Basic() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);

  // Instant CAPTCHA state (Zero network delay)
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaPayload, setCaptchaPayload] = useState({ hash: "", timestamp: 0, captchaId: "" });
  const [isCaptchaExpired, setIsCaptchaExpired] = useState(false);
  const [captchaErrorType, setCaptchaErrorType] = useState(null); // 'expired' | 'invalid' | null
  const [honeypotVal, setHoneypotVal] = useState("");
  const [userCaptchaInput, setUserCaptchaInput] = useState("");
  const lastCodeRef = useRef("");
  const captchaExpiryTimerRef = useRef(null);

  const [adminDialog, setAdminDialog] = useState({
    open: false,
    password: "",
    showPassword: false,
    loading: false,
    error: "",
  });
  const [loginState, setLoginState] = useState({
    loading: false,
    error: "",
    success: false,
  });

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const refreshCaptcha = useCallback(async () => {
    try {
      setUserCaptchaInput("");
      setIsCaptchaExpired(false);
      setCaptchaErrorType(null);

      // Clear any previous expiration timer
      if (captchaExpiryTimerRef.current) {
        clearTimeout(captchaExpiryTimerRef.current);
        captchaExpiryTimerRef.current = null;
      }

      const res = await fetch(API_ENDPOINTS.GET_CAPTCHA || "/api/auth/captcha", {
        cache: "no-store",
      });
      const data = await res.json();
      if (data.success && data.captchaImage) {
        setCaptchaImage(data.captchaImage);
        setCaptchaPayload({
          captchaId: data.captchaId,
          timestamp: Date.now(),
        });

        // ⏱️ Auto-expire CAPTCHA after exactly 2 minutes (120 seconds / 120,000 ms)
        captchaExpiryTimerRef.current = setTimeout(() => {
          setCaptchaErrorType("expired");
          setIsCaptchaExpired(true);
        }, 2 * 60 * 1000);
      }
    } catch (err) {
      console.warn("Server CAPTCHA fetch notice:", err.message);
    }
  }, []);

  useEffect(() => {
    refreshCaptcha();

    // Cleanup timer on unmount
    return () => {
      if (captchaExpiryTimerRef.current) {
        clearTimeout(captchaExpiryTimerRef.current);
      }
    };
  }, [refreshCaptcha]);

  // Auto-dismiss login error after exactly 3 seconds
  useEffect(() => {
    if (loginState.error) {
      const errorTimer = setTimeout(() => {
        setLoginState((prev) => ({ ...prev, error: "" }));
      }, 3000);
      return () => clearTimeout(errorTimer);
    }
  }, [loginState.error]);

  const handleCheckboxChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.checked,
    });
  };

  // Admin dialog handlers
  const handleAdminDialogOpen = () => {
    setAdminDialog({
      open: true,
      password: "",
      showPassword: false,
      loading: false,
      error: "",
    });
  };

  const handleAdminDialogClose = () => {
    setAdminDialog({
      open: false,
      password: "",
      showPassword: false,
      loading: false,
      error: "",
    });
  };

  const handleAdminPasswordChange = (event) => {
    setAdminDialog({
      ...adminDialog,
      password: event.target.value,
      error: "",
    });
  };

  const handleAdminSubmit = async () => {
    if (!adminDialog.password) {
      setAdminDialog({ ...adminDialog, error: "Admin password is required!" });
      return;
    }

    setAdminDialog({ ...adminDialog, loading: true, error: "" });

    try {
      const response = await fetch(API_ENDPOINTS.VERIFY_ADMIN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ adminPassword: adminDialog.password }),
      });

      const result = await response.json();

      if (result.success) {
        const adminUser = {
          username: "admin",
          role: "admin",
          id: "admin-user",
        };

        const adminToken = "admin-session-" + Date.now();
        login(adminToken, adminUser);

        handleAdminDialogClose();
        navigate("/admin-panel");
      } else {
        setAdminDialog({ ...adminDialog, loading: false, error: "Invalid admin password!" });
      }
    } catch (error) {
      setAdminDialog({ ...adminDialog, loading: false, error: "Admin verification failed!" });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // If CAPTCHA is already expired, block submission
    if (isCaptchaExpired) {
      return;
    }

    // 1. Honeypot check - Bot trap detection
    if (honeypotVal && honeypotVal.trim().length > 0) {
      setIsCaptchaExpired(true);
      setCaptchaImage("");
      setLoginState({ loading: false, error: "", success: false });
      return;
    }

    // 2. Form field validation
    if (!formData.username.trim()) {
      setLoginState({ ...loginState, error: "Username or email is required" });
      return;
    }
    if (!formData.password.trim()) {
      setLoginState({ ...loginState, error: "Password is required" });
      return;
    }

    // 3. CAPTCHA validation
    const userVal = userCaptchaInput.trim();
    if (!userVal) {
      setLoginState({ ...loginState, error: "Please enter the security code shown in the image." });
      return;
    }

    setLoginState({ loading: true, error: "", success: false });

    try {
      const captchaToken = {
        captchaId: captchaPayload.captchaId,
        userAnswer: userVal,
        timestamp: captchaPayload.timestamp,
        honeypot: honeypotVal,
      };

      // Send login credentials with CAPTCHA
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          captchaToken,
        }),
      });

      const result = await response.json();

      if (result.success) {
        login(result.token, result.user, result.monitoringRecordId);
        setLoginState({ loading: false, error: "", success: true });

        let targetPath = "/fdo-panel";
        if (result.user.role === "custom") {
          let userPerms = result.user.permissions;
          if (typeof userPerms === "string") {
            try {
              userPerms = JSON.parse(userPerms);
            } catch (e) {}
          }
          if (userPerms?.fdoPanel?.view || userPerms?.fdoPanel?.complete) {
            targetPath = "/fdo-panel";
          } else if (userPerms?.upsells?.view || userPerms?.upsells?.complete) {
            targetPath = "/upsells";
          } else if (userPerms?.cleaning?.view || userPerms?.cleaning?.complete) {
            targetPath = "/cleaning";
          } else if (userPerms?.payment?.view || userPerms?.payment?.complete) {
            targetPath = "/payments";
          } else if (userPerms?.qbPayment?.view || userPerms?.qbPayment?.complete) {
            targetPath = "/qb-payments";
          } else if (userPerms?.securityDeposit?.view || userPerms?.securityDeposit?.complete) {
            targetPath = "/security-deposit";
          } else if (userPerms?.userManagement?.complete || userPerms?.monthlyTarget?.view || userPerms?.passwordHistory?.complete) {
            targetPath = "/admin-panel";
          } else {
            targetPath = "/upsells";
          }
        }

        setTimeout(() => {
          navigate(targetPath);
        }, 1000);
      } else {
        let errorMsg = result.message || "Invalid username or password";
        const isCaptchaError = errorMsg.toLowerCase().includes("captcha");

        if (isCaptchaError) {
          setCaptchaErrorType("invalid");
          setIsCaptchaExpired(true);
          errorMsg = "The user entered an invalid CAPTCHA.";
        }

        setLoginState({
          loading: false,
          error: errorMsg,
          success: false
        });
      }
    } catch (error) {
      let errorMsg = error.message || "Invalid username or password";
      const isCaptchaError = errorMsg.toLowerCase().includes("captcha");

      if (isCaptchaError) {
        setCaptchaErrorType("invalid");
        setIsCaptchaExpired(true);
        errorMsg = "The user entered an invalid CAPTCHA.";
      }

      setLoginState({
        loading: false,
        error: errorMsg,
        success: false
      });
    }
  };

  const handleCloseError = () => {
    setLoginState({ ...loginState, error: "" });
  };

  return (
    <MDBox
      sx={{
        minHeight: "100vh",
        width: "100%",
        background: "#f8fafc",
        display: "flex",
        flexDirection: "column",
        overflowY: "auto", // Ensure scrolling is allowed
      }}
    >
      <Container maxWidth="sm" sx={{ my: "auto" }}>
        {/* Main Card */}
        <Card
          sx={{
            background: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            border: "1px solid #e2e8f0",
            maxWidth: "400px",
            margin: "0 auto",
          }}
        >
          <MDBox p={4}>
            {/* Dashboard Logo and Header */}
            <MDBox textAlign="center" mb={4}>
              {/* Custom Logo */}
              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                {/* Custom Logo */}
                <Logo
                  height="150px"
                  maxWidth="350px"
                />
              </MDBox>

              {/* Greeting */}
              <MDTypography
                variant="h4"
                sx={{
                  color: "#000000",
                  fontWeight: "400",
                  fontSize: "1.875rem",
                  mb: 1,
                }}
              >
                Sign into your account
              </MDTypography>
            </MDBox>





            {/* Success Alert */}
            {loginState.success && (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  borderRadius: "8px",
                  "& .MuiAlert-message": {
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  },
                }}
              >
                Login successful! Redirecting...
              </Alert>
            )}

            {/* Form */}
            <MDBox component="form" onSubmit={handleSubmit}>
              {/* Invisible Honeypot Trap Field (Catches Spam Bots) */}
              <input
                type="text"
                name="b_website_security_hp"
                value={honeypotVal}
                onChange={(e) => setHoneypotVal(e.target.value)}
                style={{ display: "none", position: "absolute", left: "-9999px", opacity: 0 }}
                tabIndex={-1}
                autoComplete="off"
              />

              {/* Username Field */}
              <MDBox mb={3}>
                <MDTypography
                  variant="body2"
                  sx={{
                    color: "#000000",
                    fontWeight: "500",
                    mb: 1,
                    fontSize: "0.875rem",
                  }}
                >
                  Username or email address
                </MDTypography>
                <MDInput
                  type="text"
                  placeholder="Username or email address"
                  fullWidth
                  value={formData.username}
                  onChange={handleInputChange("username")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      backgroundColor: "#ffffff",
                      fontSize: "0.875rem",
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: "12px 16px",
                      fontSize: "0.875rem",
                      color: "#374151",
                      "&::placeholder": {
                        color: "#9ca3af",
                        opacity: 1,
                      },
                    },
                  }}
                />
              </MDBox>

              {/* Password Field */}
              <MDBox mb={4} position="relative">
                <MDTypography
                  variant="body2"
                  sx={{
                    color: "#000000",
                    fontWeight: "500",
                    mb: 1,
                    fontSize: "0.875rem",
                  }}
                >
                  Password
                </MDTypography>
                <MDInput
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  fullWidth
                  value={formData.password}
                  onChange={handleInputChange("password")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      backgroundColor: "#ffffff",
                      fontSize: "0.875rem",
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover": {
                        borderColor: "#9ca3af",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused": {
                        borderColor: "#476746", // Exact color specified by user
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: "12px 16px",
                      fontSize: "0.875rem",
                      color: "#374151",
                      "&::placeholder": {
                        color: "#9ca3af",
                        opacity: 1,
                      },
                    },
                  }}
                />
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "calc(50% + 12px)", // Adjusted to account for label
                    transform: "translateY(-50%)",
                    zIndex: 1,
                    color: "#6b7280",
                    padding: "8px",
                    "&:hover": {
                      backgroundColor: "rgba(107, 114, 128, 0.1)",
                      color: "#374151",
                    },
                  }}
                >
                  {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </MDBox>

              {/* Image CAPTCHA Widget or CAPTCHA Expired / Invalid Box */}
              {isCaptchaExpired ? (
                <MDBox
                  sx={{
                    mb: 3,
                    py: 2.2,
                    px: 2.5,
                    backgroundColor: "#fff5f5",
                    border: "1.5px solid #ff8080",
                    borderRadius: "10px",
                    textAlign: "center",
                    boxShadow: "0 1px 4px rgba(255, 59, 48, 0.04)",
                  }}
                >
                  <MDTypography
                    variant="h6"
                    sx={{
                      color: "#ff3b30",
                      fontWeight: 700,
                      fontSize: "1.05rem",
                      mb: 0.5,
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                    }}
                  >
                    {captchaErrorType === "invalid" ? "Invalid Captcha" : "CAPTCHA Expired"}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "#4b5563",
                      fontSize: "0.825rem",
                      lineHeight: 1.45,
                      fontWeight: 400,
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                    }}
                  >
                    Please refresh the browser to get a new captcha.
                  </MDTypography>
                </MDBox>
              ) : (
                <MDBox mb={2.5}>
                  {/* Visual Security Image Pill & Refresh Button */}
                  <MDBox
                    display="flex"
                    alignItems="center"
                    gap={1}
                    p={0.75}
                    mb={1.5}
                    sx={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                    }}
                  >
                    <MDBox
                      flex={1}
                      height="48px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: "6px",
                        overflow: "hidden",
                        border: "1px solid #f1f5f9",
                      }}
                    >
                      {captchaImage ? (
                        <img
                          src={captchaImage}
                          alt="Security Code"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "fill",
                            display: "block",
                            userSelect: "none",
                            pointerEvents: "none",
                          }}
                        />
                      ) : (
                        <MDTypography variant="caption" color="text">
                          Generating code...
                        </MDTypography>
                      )}
                    </MDBox>

                    {/* Refresh Button */}
                    <IconButton
                      onClick={refreshCaptcha}
                      size="small"
                      title="Get new code"
                      sx={{
                        color: "#64748b",
                        padding: "8px",
                        borderRadius: "6px",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                          color: "#0f172a",
                        },
                      }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </MDBox>

                  {/* Answer input field */}
                  <MDInput
                    type="text"
                    placeholder="Enter Security Code"
                    value={userCaptchaInput}
                    onChange={(e) => setUserCaptchaInput(e.target.value)}
                    fullWidth
                    required
                    autoComplete="off"
                    disabled={loginState.loading}
                    inputProps={{ maxLength: 10 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        backgroundColor: "#ffffff",
                        fontSize: "0.875rem",
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover": {
                          borderColor: "#9ca3af",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused": {
                          borderColor: "#476746",
                        },
                        "&.Mui-focused fieldset": {
                          border: "none",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: "12px 16px",
                        fontSize: "0.875rem",
                        color: "#374151",
                        "&::placeholder": {
                          color: "#9ca3af",
                          opacity: 1,
                        },
                      },
                    }}
                  />

                  {/* Hidden Honeypot Field for Bot Trapping */}
                  <input
                    type="text"
                    value={honeypotVal}
                    onChange={(e) => setHoneypotVal(e.target.value)}
                    style={{ display: "none", opacity: 0, position: "absolute", left: "-9999px" }}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </MDBox>
              )}

              {/* Login Button */}
              <MDBox mb={4}>
                <MDButton
                  type="submit"
                  fullWidth
                  size="large"
                  disabled={loginState.loading || isCaptchaExpired}
                  sx={{
                    background: (loginState.loading || isCaptchaExpired) ? "#d1d5db !important" : "#8a8a8a !important",
                    borderRadius: "6px",
                    textTransform: "none",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    py: 1.5,
                    color: "#ffffff !important",
                    boxShadow: "none !important",
                    transition: "none !important",
                    position: "relative",
                    "&:hover": {
                      background: (loginState.loading || isCaptchaExpired) ? "#d1d5db !important" : "#8a8a8a !important",
                      backgroundColor: (loginState.loading || isCaptchaExpired) ? "#d1d5db !important" : "#8a8a8a !important",
                      boxShadow: "none !important",
                      transform: "none !important",
                      filter: "none !important",
                    },
                    "&:active": {
                      background: (loginState.loading || isCaptchaExpired) ? "#d1d5db !important" : "#8a8a8a !important",
                      backgroundColor: (loginState.loading || isCaptchaExpired) ? "#d1d5db !important" : "#8a8a8a !important",
                    },
                    "&:focus": {
                      background: (loginState.loading || isCaptchaExpired) ? "#d1d5db !important" : "#8a8a8a !important",
                      backgroundColor: (loginState.loading || isCaptchaExpired) ? "#d1d5db !important" : "#8a8a8a !important",
                    },
                    "&.Mui-disabled": {
                      color: "#ffffff !important",
                    },
                  }}
                >
                  Login
                </MDButton>
              </MDBox>

              {/* Forgot Password Link */}
              <MDBox mt={2} textAlign="center">
                <MDButton
                  component={Link}
                  to="/forgot-password"
                  variant="text"
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontSize: "0.875rem",
                    fontWeight: "400",
                    color: "#1f93ff",
                    cursor: "pointer",
                    transition: "none !important",
                    "&:hover": {
                      backgroundColor: "transparent !important",
                      textDecoration: "none !important",
                      boxShadow: "none !important",
                      transform: "none !important",
                      filter: "none !important",
                      opacity: "1 !important",
                      color: "#1f93ff !important",
                    },
                    "&:focus": {
                      backgroundColor: "transparent !important",
                      boxShadow: "none !important",
                      color: "#1f93ff !important",
                    },
                    "&:active": {
                      backgroundColor: "transparent !important",
                      boxShadow: "none !important",
                      color: "#1f93ff !important",
                    },
                    "& .MuiTouchRipple-root": {
                      display: "none !important",
                    },
                  }}
                >
                  Forgot your password?
                </MDButton>
              </MDBox>

              {/* Admin Access Button - Keep for functionality */}
              <MDBox mt={1} textAlign="center">
                <MDButton
                  variant="text"
                  size="small"
                  onClick={handleAdminDialogOpen}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    fontWeight: "400",
                    color: "#6b7280",
                    cursor: "pointer",
                    transition: "none !important",
                    "&:hover": {
                      backgroundColor: "transparent !important",
                      textDecoration: "none !important",
                      boxShadow: "none !important",
                      transform: "none !important",
                      filter: "none !important",
                      opacity: "1 !important",
                      color: "#6b7280 !important",
                    },
                    "&:focus": {
                      backgroundColor: "transparent !important",
                      boxShadow: "none !important",
                      color: "#6b7280 !important",
                    },
                    "&:active": {
                      backgroundColor: "transparent !important",
                      boxShadow: "none !important",
                      color: "#6b7280 !important",
                    },
                    "& .MuiTouchRipple-root": {
                      display: "none !important",
                    },
                  }}
                >
                  Admin Access
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </Container>

      {/* Admin Password Dialog */}
      <Dialog
        open={adminDialog.open}
        onClose={handleAdminDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            pb: 2,
            color: "#1f2937",
            fontSize: "1.25rem",
            fontWeight: "600",
          }}
        >
          Admin Access
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <MDTypography variant="body2" color="text" mb={3} textAlign="center">
            Enter admin password to access the admin panel
          </MDTypography>

          {adminDialog.error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {adminDialog.error}
            </Alert>
          )}

          <MDBox position="relative">
            <MDInput
              type={adminDialog.showPassword ? "text" : "password"}
              label="Admin Password"
              fullWidth
              value={adminDialog.password}
              onChange={handleAdminPasswordChange}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleAdminSubmit();
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
              }}
            />
            <IconButton
              onClick={() =>
                setAdminDialog({
                  ...adminDialog,
                  showPassword: !adminDialog.showPassword,
                })
              }
              sx={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1,
                color: "#6b7280",
                padding: "8px",
                "&:hover": {
                  backgroundColor: "rgba(107, 114, 128, 0.1)",
                  color: "#374151",
                },
              }}
            >
              {adminDialog.showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <MDButton
            variant="text"
            color="secondary"
            onClick={handleAdminDialogClose}
            disabled={adminDialog.loading}
            sx={{
              textTransform: "none",
              fontSize: "0.9rem",
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleAdminSubmit}
            disabled={adminDialog.loading}
            sx={{
              background: "#1f93ff",
              borderRadius: "6px",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: "500",
              px: 3,
              color: "#ffffff",
              "&:hover": {
                background: "#1e40af",
              },
            }}
          >
            {adminDialog.loading ? "Verifying..." : "Access Admin Panel"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!loginState.error}
        autoHideDuration={3000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%", borderRadius: "8px" }}
        >
          {loginState.error}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}

export default Basic;
